/** Query param whitelist and sanitization; all `location.search` reads go through `getSafeSearchParams()`. */
import { captureEntryParams } from './entryParams';
export const ALLOWED_SEARCH_PARAMS: ReadonlySet<string> = new Set([
  'utm_source',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_campaign_name',
  'utm_campaign_id',
  'utm_adset_name',
  'utm_adset_id',
  'utm_ad_name',
  'utm_ad_id',
  'utm_channel',
  'utm_medium',
  'utm_id',
  'debug_product_name',
  'onboardingVariant',
  'debug',
  'preview',
  // Preview only: onboarding generated by KA (`/v1/onboarding/generate`), see config/query.ts.
  'onboardingId',
  'popupVariant',
  'd-offer',
  'dOffer',
  'sub1',
  'fbclid',
  'ttclid',
  // Snapchat click id, spelled exactly as Snapchat appends it; it goes into the conversion payload.
  'ScCid',
  'aclid',
  'enable_finish_flow_modal',
  'enable_gateway_api_url',
  'fbp',
  'fbc',
  'specialOfferModal',
  'return_popup',
  'fb_event_test_code',
  'lang',
  'theme',
  'config',
  'amp_config',
  // Amplitude device id handed over by the landing: keeps the experiment bucket and the analytics
  // session continuous across the hop (WebQuiz reads the same param).
  'amp_device_id',
  '_gl',
  'vwo_uuid',
  'gad_campaignid',
  'gad_source',
  'gbraid',
  'gclid',
  'gtm_debug',
  // Promo scratch
  'discount',
  'scratch_x',
  'scratch_n',
]);

export const SUSPICIOUS_STRINGS = [
  'javascript:',
  'vbscript:',
  'data:',
  '<script',
  '<iframe',
  'eval(',
  'expression(',
] as const;

export const SUSPICIOUS_PATTERNS_REGEXP = [/on\w+\s*=/i] as const;

export const MAX_PARAM_VALUE_LENGTH = 1000;

export const isValidParamValue = (value: string): boolean => {
  if (!value || value.length > MAX_PARAM_VALUE_LENGTH) return false;
  const lowerValue = value.toLowerCase();
  for (const str of SUSPICIOUS_STRINGS) {
    if (lowerValue.includes(str)) return false;
  }
  for (const pattern of SUSPICIOUS_PATTERNS_REGEXP) {
    if (pattern.test(value)) return false;
  }
  return true;
};

/** Keeps only whitelisted keys with safe values. */
export const filterAllowedSearchParams = (search: string | URLSearchParams): URLSearchParams => {
  const filtered = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) {
    if (ALLOWED_SEARCH_PARAMS.has(key) && isValidParamValue(value)) {
      filtered.set(key, value);
    }
  }
  return filtered;
};

/** `utm_source` → `utmSource`; keys without separators stay as is. For `analytics.init`. */
export const camelCaseSearchParams = (search: string | URLSearchParams): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (!/[\s_-]/.test(key)) {
      result[key] = value;
      continue;
    }
    const camelCasedKey = key
      .replace(/[^a-zA-Z0-9]/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word, index) =>
        index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join('');
    result[camelCasedKey] = value;
  }
  return result;
};

let cached: URLSearchParams | undefined;

/**
 * Filtered entry params; computed once per page load. Read from the URL merged over the stored
 * copy of the previous visit, after which the URL is cleaned (see `entryParams.ts`).
 */
export const getSafeSearchParams = (): URLSearchParams => {
  if (!cached) {
    try {
      cached = captureEntryParams(window, window.sessionStorage);
    } catch {
      cached = new URLSearchParams();
    }
  }
  return cached;
};

/** Test-only. */
export const resetSafeSearchParamsCache = (): void => {
  cached = undefined;
};
